import React, { useEffect, useState } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { makeAuthenticatedRequest } from '../services/api'; // Use the API service
import { FaLink, FaFileInvoice, FaCloudDownloadAlt } from 'react-icons/fa';
import { initiateXeroConnection, handleXeroRedirectStatus } from '../utils/xeroUtils';
import useDemoPermissions from '../hooks/useDemoPermissions';

const SettingsPage = () => {
  const { isDemo } = useDemoPermissions();
  const location = useLocation();
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroMessage, setXeroMessage] = useState('');
  const [isLoadingConnect, setIsLoadingConnect] = useState(false);
  const [isLoadingTest, setIsLoadingTest] = useState(false);
  const [testInvoiceResult, setTestInvoiceResult] = useState(null);

  // Check for redirect status on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const redirectStatus = handleXeroRedirectStatus(queryParams);

    if (redirectStatus) {
      setXeroStatus(redirectStatus.success ? 'success' : 'error');
      setXeroMessage(redirectStatus.message);
      // Clear query params from URL after reading them
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  const handleConnectXero = async () => {
    setIsLoadingConnect(true);
    setXeroStatus(null); // Reset status
    setXeroMessage('');
    setTestInvoiceResult(null); // Reset test result
    try {
      const authUrl = await initiateXeroConnection();
      // Redirect the browser to the URL provided by the backend
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating Xero connection:', error);
      setXeroStatus('error');
      setXeroMessage(error.message);
      setIsLoadingConnect(false); // Stop loading only on error
    }
  };

  const handleCreateTestInvoice = async () => {
    setIsLoadingTest(true);
    setTestInvoiceResult(null);
    setXeroStatus(null); // Clear previous Xero connection status messages
    setXeroMessage('');
    try {
      const response = await makeAuthenticatedRequest('/xero/invoices/test', {
        method: 'POST',
      });

      setTestInvoiceResult({ status: 'success', data: response });
    } catch (error) {
      console.error('Error creating test Xero invoice:', error);
      const message = error.response?.data?.message || error.message || 'An unknown error occurred.';
      setTestInvoiceResult({ status: 'error', message: message });
    } finally {
      setIsLoadingTest(false);
    }
  };

  if (isDemo) return <Navigate to="/" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
          Settings
        </h1>
        <Link to="/" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
          Back to Dashboard
        </Link>
      </div>

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6 space-y-6 mt-8">

        {/* Xero Integration Section */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Xero Integration</h2>

          {/* Display feedback from redirect */}
          {xeroStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
              Xero connected successfully! {xeroMessage && `(${xeroMessage})`}
            </div>
          )}
          {xeroStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              Xero connection failed: {xeroMessage}
            </div>
          )}

          <button
            onClick={handleConnectXero}
            disabled={isLoadingConnect}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm inline-flex items-center transition duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FaLink className="mr-2" />
            {isLoadingConnect ? 'Connecting...' : 'Connect to Xero'}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Connect your Xero account to enable invoice synchronization. You will be redirected to Xero to authorize the connection.
          </p>

          {/* New Button to Import Contacts */}
          <Link
            to="/settings/xero-import-contacts"
            className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 inline-flex items-center transition duration-300 mt-4"
          >
            <FaCloudDownloadAlt className="mr-2" />
            Xero Contacts
          </Link>
          <p className="text-xs text-gray-500 mt-2">
            Fetch contacts from Xero and stage them in the system.
          </p>
        </div>

        {/* Test Invoice Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Functions</h2>
          <button
            onClick={handleCreateTestInvoice}
            disabled={isLoadingTest || isLoadingConnect}
            className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 inline-flex items-center transition duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FaFileInvoice className="mr-2" />
            {isLoadingTest ? 'Creating...' : 'Create Test Xero Invoice'}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Create a sample invoice in your connected Xero account for testing purposes. Requires Xero connection.
          </p>

          {/* Display test invoice result */}
          {testInvoiceResult?.status === 'success' && (
            <div className="mt-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
              <p className="font-semibold">Test invoice created successfully!</p>
              {testInvoiceResult.data?.invoiceId && <p>Invoice ID: {testInvoiceResult.data.invoiceId}</p>}
              {testInvoiceResult.data?.invoiceNumber && <p>Invoice Number: {testInvoiceResult.data.invoiceNumber}</p>}
            </div>
          )}
          {testInvoiceResult?.status === 'error' && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              <p className="font-semibold">Failed to create test invoice:</p>
              <p>{testInvoiceResult.message}</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default SettingsPage;
